<template>
  <section class="sales-toolbar app-filter-toolbar">
    <div class="toolbar-main-row app-filter-toolbar-row">
      <div class="search-wrap app-filter-search-wrap">
        <i class="fa fa-search" aria-hidden="true"></i>
        <input
          v-model="search"
          type="text"
          :placeholder="toolbarText.searchPlaceholder"
          @input="emitSearch"
          @keyup.enter="emitApply"
        />
      </div>

      <div class="toolbar-right app-filter-toolbar-right">
        <AppFilterDateRange
          v-model="dateRangeValue"
          class="app-filter-date-inline"
          label=""
          :show-input="showDateInput"
          :presets="dateRangePresets"
          :initial-preset-key="initialDatePresetKey"
          @change="onDateRangeChange"
        />

        <multiselect
          v-if="showCustomerFilter"
          v-model="filters.customer"
          :options="options.customer"
          track-by="value"
          label="label"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :preserve-search="true"
          :show-labels="false"
          :searchable="customerFilterConfig?.searchable"
          :allow-empty="true"
          :max-height="220"
          :placeholder="customerFilterConfig?.placeholder"
          class="sales-toolbar-customer-inline customer-filter-select app-filter-select app-select app-select--filters"
          @select="onSelect('customer', $event)"
          @remove="onRemove('customer')"
        >
          <template #selection="{ values, isOpen }">
            <span v-if="!isOpen" class="multiselect__single">
              {{ selectionLabel('customer', values) }}
            </span>
          </template>
          <template #noResult>
            <span>{{ commonText.noOption }}</span>
          </template>
          <template #noOptions>
            <span>{{ commonText.noOption }}</span>
          </template>
        </multiselect>

        <button
          type="button"
          class="icon-btn app-filter-icon-btn"
          :class="{ active: showFilters }"
          :title="toolbarText.filtersTitle"
          @click="toggleFilters"
        >
          <i class="fa fa-filter" aria-hidden="true"></i>
        </button>

        <button
          type="button"
          class="icon-btn app-filter-icon-btn sales-toolbar-search-btn"
          :title="toolbarText.searchTitle"
          @click="emitApply"
        >
          <i class="fa fa-search" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <transition
      name="filters-expand"
      @after-leave="handleFiltersAfterLeave"
    >
      <div v-show="showFilters" class="filters-panel app-filter-panel">
        <div class="sales-filters-grid sales-filters-grid--first-row">
          <div
            v-for="filter in firstRowFilterConfigs"
            :key="filter.key"
            class="filter-field app-filter-field"
          >
            <span>{{ filter.label }}</span>
            <multiselect
              v-model="filters[filter.key]"
              :options="options[filter.key]"
              track-by="value"
              label="label"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :show-labels="false"
              :searchable="filter.searchable"
              :allow-empty="true"
              :max-height="220"
              :placeholder="filter.placeholder"
              class="customer-filter-select app-filter-select app-select app-select--filters"
              @select="onSelect(filter.key, $event)"
              @remove="onRemove(filter.key)"
            >
              <template #selection="{ values, isOpen }">
                <span v-if="!isOpen" class="multiselect__single">
                  {{ selectionLabel(filter.key, values) }}
                </span>
              </template>
              <template #noResult>
                <span>{{ commonText.noOption }}</span>
              </template>
              <template #noOptions>
                <span>{{ commonText.noOption }}</span>
              </template>
            </multiselect>
          </div>
        </div>

        <div class="sales-filters-grid sales-filters-grid--second-row">
          <div
            v-for="filter in secondRowFilterConfigs"
            :key="filter.key"
            class="filter-field app-filter-field"
          >
            <span>{{ filter.label }}</span>
            <multiselect
              v-model="filters[filter.key]"
              :options="options[filter.key]"
              track-by="value"
              label="label"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :show-labels="false"
              :searchable="filter.searchable"
              :allow-empty="true"
              :max-height="220"
              :placeholder="filter.placeholder"
              class="customer-filter-select app-filter-select app-select app-select--filters"
              @select="onSelect(filter.key, $event)"
              @remove="onRemove(filter.key)"
            >
              <template #selection="{ values, isOpen }">
                <span v-if="!isOpen" class="multiselect__single">
                  {{ selectionLabel(filter.key, values) }}
                </span>
              </template>
              <template #noResult>
                <span>{{ commonText.noOption }}</span>
              </template>
              <template #noOptions>
                <span>{{ commonText.noOption }}</span>
              </template>
            </multiselect>
          </div>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
import areasService from '@/services/areas';
import collaboratorsService from '@/services/collaborators';
import customersService from '@/services/customers';
import salesService from '@/services/sales';
import salesMultiCustomerService from '@/services/salesMultiCustomer';
import servicesService from '@/services/services';
import { extractCollection } from '@/utils/dataNormalization';
import {
  ALL_OPTION_VALUE,
  applyRemoveOption,
  applySelectOption,
  buildSelectedValues,
  ensureDefaultSelection,
  withAllOption,
} from '@/utils/filterSelectors';
import AppFilterDateRange from '@/components/common/AppFilterDateRange.vue';
import {
  buildToolbarApplyPayload,
  buildToolbarSelectionLabel,
  createToolbarAllOption,
  extractToolbarDateRange,
  mapToolbarOptions,
  normalizeToolbarDate,
  resolveToolbarToggleState,
  shouldEmitToolbarDateChange,
} from '@/composables/shared/useFilterToolbarShared';
import { SALES_TEXT } from '@/texts/catalog/sales';

const SHARED_SALES_TEXT = SALES_TEXT.shared;
const TOOLBAR_TEXT = SHARED_SALES_TEXT.toolbar;
const COMMON_TEXT = SHARED_SALES_TEXT.common;

export default {
  name: 'SharedSalesToolbar',
  components: {
    AppFilterDateRange,
  },
  props: {
    mode: {
      type: String,
      default: 'sales',
      validator(value) {
        return ['sales', 'multi-customer', 'flat-rates'].includes(String(value || '').trim());
      },
    },
    showCustomerFilter: {
      type: Boolean,
      default: true,
    },
    initialDatePresetKey: {
      type: String,
      default: 'this_year',
    },
  },
  emits: ['search-change', 'filters-change', 'apply'],
  computed: {
    canFilterArea() {
      return typeof this.$can !== 'function' || this.$can('customers.areas.assign');
    },
    customerFilterConfig() {
      return this.filterConfigByKey('customer');
    },
    firstRowFilterConfigs() {
      const keys = ['service', 'status', 'seller'];

      return keys
        .map((key) => this.filterConfigByKey(key))
        .filter(Boolean);
    },
    secondRowFilterConfigs() {
      const keys = ['owner'];

      if (this.canFilterArea) {
        keys.push('area');
      }

      return keys
        .map((key) => this.filterConfigByKey(key))
        .filter(Boolean);
    },
    statusService() {
      return this.mode === 'multi-customer'
        ? salesMultiCustomerService
        : salesService;
    },
  },
  data() {
    const allService = createToolbarAllOption();
    const allStatus = createToolbarAllOption();
    const allCustomer = { value: ALL_OPTION_VALUE, label: TOOLBAR_TEXT.customerAllLabel };
    const allSeller = createToolbarAllOption();
    const allOwner = createToolbarAllOption();
    const allArea = createToolbarAllOption();

    return {
      toolbarText: TOOLBAR_TEXT,
      commonText: COMMON_TEXT,
      search: '',
      showFilters: false,
      showDateInput: false,
      dateRangeValue: null,
      dateRangePresets: TOOLBAR_TEXT.dateRangePresets,
      options: {
        service: [allService],
        status: [allStatus],
        customer: [allCustomer],
        seller: [allSeller],
        owner: [allOwner],
        area: [allArea],
      },
      filters: {
        service: [allService],
        status: [allStatus],
        customer: [allCustomer],
        seller: [allSeller],
        owner: [allOwner],
        area: [allArea],
      },
      filterConfigs: [
        {
          key: 'service',
          label: TOOLBAR_TEXT.filters.service.label,
          placeholder: TOOLBAR_TEXT.filters.service.placeholder,
          searchable: true,
        },
        {
          key: 'status',
          label: TOOLBAR_TEXT.filters.status.label,
          placeholder: TOOLBAR_TEXT.filters.status.placeholder,
          searchable: true,
        },
        {
          key: 'customer',
          label: TOOLBAR_TEXT.filters.customer.label,
          placeholder: TOOLBAR_TEXT.filters.customer.placeholder,
          searchable: true,
        },
        {
          key: 'seller',
          label: TOOLBAR_TEXT.filters.seller.label,
          placeholder: TOOLBAR_TEXT.filters.seller.placeholder,
          searchable: true,
        },
        {
          key: 'owner',
          label: TOOLBAR_TEXT.filters.owner.label,
          placeholder: TOOLBAR_TEXT.filters.owner.placeholder,
          searchable: true,
        },
        {
          key: 'area',
          label: TOOLBAR_TEXT.filters.area.label,
          placeholder: TOOLBAR_TEXT.filters.area.placeholder,
          searchable: true,
        },
      ],
    };
  },
  mounted() {
    this.loadFilterOptions();
  },
  methods: {
    toggleFilters() {
      const nextState = resolveToolbarToggleState(this.showFilters);
      this.showFilters = nextState.showFilters;

      if (nextState.showDateInput !== null) {
        this.showDateInput = nextState.showDateInput;
      }
    },
    handleFiltersAfterLeave() {
      this.showDateInput = false;
    },
    emitSearch() {
      this.$emit('search-change', this.search);
    },
    normalizeDate(value) {
      return normalizeToolbarDate(value);
    },
    onDateRangeChange(payload) {
      this.dateRangeValue = Array.isArray(payload?.value) ? payload.value : null;

      if (shouldEmitToolbarDateChange(payload, this.dateRangeValue)) {
        this.emitFilters('date');
      }
    },
    extractDateRange() {
      return extractToolbarDateRange(this.dateRangeValue, {
        startKey: 'start',
        endKey: 'end',
      });
    },
    filterConfigByKey(key) {
      return this.filterConfigs.find((filter) => filter.key === key) || null;
    },
    async loadFilterOptions() {
      const requests = [
        ['service', servicesService.index()],
        ['status', this.statusService.statuses()],
        ['seller', collaboratorsService.sellers()],
        ['owner', collaboratorsService.managers()],
      ];

      if (this.showCustomerFilter) {
        requests.push(['customer', customersService.selectors()]);
      }

      if (this.canFilterArea) {
        requests.push(['area', areasService.index()]);
      }

      const results = await Promise.allSettled(requests.map(([, request]) => request));

      requests.forEach(([key], index) => {
        const result = results[index];
        const collection = result?.status === 'fulfilled' ? extractCollection(result.value) : [];
        const normalized = mapToolbarOptions(collection);
        if (key === 'customer') {
          this.options[key] = [{ value: ALL_OPTION_VALUE, label: this.toolbarText.customerAllLabel }, ...normalized];
          return;
        }

        this.options[key] = withAllOption(normalized);
      });

      if (!this.canFilterArea) {
        this.options.area = [createToolbarAllOption()];
        this.filters.area = [this.options.area[0]];
      }

      this.ensureAllDefault('service');
      this.ensureAllDefault('status');
      if (this.showCustomerFilter) {
        this.ensureAllDefault('customer');
      }
      this.ensureAllDefault('seller');
      this.ensureAllDefault('owner');
      this.ensureAllDefault('area');
      this.emitFilters('init');
    },
    ensureAllDefault(key) {
      this.filters[key] = ensureDefaultSelection(this.filters[key], this.options[key]?.[0]);
    },
    onSelect(key, option) {
      this.filters[key] = applySelectOption(this.filters[key], option, this.options[key]?.[0]);
    },
    onRemove(key) {
      this.filters[key] = applyRemoveOption(this.filters[key], this.options[key]?.[0]);
    },
    selectionLabel(key, values) {
      if (key === 'customer') {
        const selected = Array.isArray(values) ? values : [];
        if (
          selected.length === 0
          || selected.some((option) => option?.value === ALL_OPTION_VALUE)
        ) {
          return buildToolbarSelectionLabel(values, this.toolbarText.customerAllLabel);
        }
      }

      return buildToolbarSelectionLabel(values);
    },
    selectedValues(key) {
      return buildSelectedValues(this.filters[key]);
    },
    emitFilters(trigger = 'manual') {
      const dateRange = this.extractDateRange();

      const payload = {
        search: this.search,
        date_start: dateRange.start,
        date_end: dateRange.end,
        service: this.selectedValues('service'),
        status: this.selectedValues('status'),
        customer: this.showCustomerFilter ? this.selectedValues('customer') : [],
        seller: this.selectedValues('seller'),
        owner: this.selectedValues('owner'),
        area: this.canFilterArea ? this.selectedValues('area') : [],
      };

      this.$emit('filters-change', payload, { trigger });
      return payload;
    },
    emitApply() {
      const filters = this.emitFilters('apply');

      this.$emit('search-change', this.search);
      this.$emit('apply', buildToolbarApplyPayload(this.search, filters));
    },
  },
};
</script>

<style scoped>
.sales-filters-grid {
  display: grid;
  gap: 10px;
}

.sales-filters-grid--first-row {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.sales-filters-grid--second-row {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 10px;
}

.sales-toolbar-customer-inline {
  width: 238px;
  min-width: 238px;
  flex: 0 0 238px;
  margin-top: 4px;
}

.sales-toolbar-customer-inline :deep(.multiselect__tags) {
  min-height: 36px;
  height: auto;
}

.sales-toolbar-search-btn {
  background-color: #0070cd;
  border-color: #0070cd;
  color: #ffffff;
}

.sales-toolbar-search-btn i {
  color: #ffffff;
}

.sales-toolbar-search-btn:hover,
.sales-toolbar-search-btn:focus {
  background-color: #005eae;
  border-color: #005eae;
}

@media (max-width: 1200px) {
  .sales-filters-grid--first-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .sales-filters-grid--second-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .sales-filters-grid--first-row,
  .sales-filters-grid--second-row {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
